import { getAccountId } from '../../utils/serverAPI/getAccountId';
import { getContactPaymentDetails } from '../../utils/serverAPI/paypal';
import { type ShippingContact } from '../../types/Shipping';
import { type BillingContact } from '../../types/BillingContact';
import { logXhrError } from '../../utils/sentry';
import { type StoreState } from '../../types/StoreState';
import { selectAuthParams } from '../../selectors';
import { splitToFirstAndLastName } from '../../utils/splitToFirstAndLastName';
import { selectPayPalPaymentData } from '../../selectors/payment';

export interface GetContactPaymentDetailsParams {
  orderId: string;
  appDefId: string;
  appInstanceId: string;
}

/**
 * Sends request to Cashier backend to get PayPal payment details
 * including shipping contact data, billing data.
 * @param params PaymentDetailsParams
 */
export const getPayPalContactPaymentDetails =
  (params: GetContactPaymentDetailsParams) =>
  async (
    dispatch,
    getState: () => StoreState,
  ): Promise<{ shippingContact: ShippingContact; billingContact: BillingContact }> => {
    const accountId = getAccountId(params.appDefId, params.appInstanceId);
    const { chargeId, cashierOrderId } = selectPayPalPaymentData(getState());
    const reqArgs: Parameters<typeof getContactPaymentDetails>[0] = {
      accountId,
      orderId: params.orderId,
    };

    if (chargeId && cashierOrderId) {
      reqArgs.chargeId = chargeId;
      reqArgs.cashierOrderId = cashierOrderId;
    }

    try {
      const contact = (await getContactPaymentDetails(reqArgs, selectAuthParams(getState()))) as {
        shippingContact: ShippingContact;
        billingContact: BillingContact;
      };
      // https://wix.atlassian.net/browse/PAY-1610
      // in case if requestShipping === false,
      // all contact.shippingContact props will have null values
      if (contact.shippingContact.fullName) {
        const name = splitToFirstAndLastName(contact.shippingContact.fullName);
        contact.shippingContact.firstName = name.firstName;
        contact.shippingContact.lastName = name.lastName;
      }
      return contact;
    } catch (e) {
      logXhrError({
        message: 'XHR: PayPal getPayPalContactPaymentDetails failed',
        axiosError: e,
        fingerprint: ['getPayPalContactPaymentDetails'],
        reqArgs,
      });
      throw e;
    }
  };
